import { useMutation, useQuery, useQueryClient } from "react-query";
import type {
  ChooseProcurementInputRequest,
  ProcurementFindingDismissalRequest,
  ProcurementGetResult,
} from "../models/procurement";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

const PROCUREMENT_QUERY_KEY = QK.procurement;

export const useProcurementAssessment = (project_id?: string) => {
  const http = useAgentsHttp();
  return useQuery<ProcurementGetResult | null>(
    [PROCUREMENT_QUERY_KEY, project_id ?? null],
    async () => {
      if (!project_id) return null;
      return http<ProcurementGetResult>("/projects/procurement/get", {
        project_id,
      });
    },
    {
      enabled: Boolean(project_id),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// The /projects/procurement/run endpoint now stages a Tier-2 LRO and
// returns {operation_key, status: "pending"} immediately. The FE
// receives the operation_key, drives polling via useAsyncOperation,
// and refetches /projects/procurement/get once the operation reaches
// "completed" so the persisted assessment renders.
export interface RunProcurementResponse {
  project_id: string;
  status: "pending";
  operation_key: string;
}

export const useRunProcurementAssessment = () => {
  const http = useAgentsHttp();
  return useMutation<RunProcurementResponse, Error, { project_id: string }>(
    (input) =>
      http<RunProcurementResponse>("/projects/procurement/run", input),
  );
};

export const useInvalidateProcurementAssessment = () => {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({ queryKey: [PROCUREMENT_QUERY_KEY] });
};

// Cross-site comparison stage endpoint. Returns an operation_key the
// FE then polls via useAsyncOperation. The actual CrossSiteResult
// lives in the operation's ``triage`` field once status === "completed".
export interface CompareProcurementPendingResponse {
  status: "pending";
  operation_key: string;
}

export interface CompareProcurementIneligibleResponse {
  status: "ineligible";
  ineligible_projects: Array<{ project_id: string; reason: string }>;
}

export type CompareProcurementResponse =
  | CompareProcurementPendingResponse
  | CompareProcurementIneligibleResponse;

// Toggle the dismissed-state of a single finding on a persisted
// procurement assessment. On success the BE returns the GET's own
// payload; we push it straight into the procurement-get cache so
// the tab re-renders without an extra refetch round-trip.
export const useDismissProcurementFinding = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    ProcurementGetResult,
    Error,
    ProcurementFindingDismissalRequest
  >(
    (input) =>
      http<ProcurementGetResult>(
        "/projects/procurement/finding-dismissal",
        input,
      ),
    {
      onSuccess: (data) => {
        qc.setQueryData([PROCUREMENT_QUERY_KEY, data.project_id], data);
      },
    },
  );
};

// Returns the GET's payload, written straight into its cache.
export const useChooseProcurementInput = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ProcurementGetResult, Error, ChooseProcurementInputRequest>(
    (input) =>
      http<ProcurementGetResult>("/projects/procurement/choose-input", input),
    {
      onSuccess: (data) => {
        qc.setQueryData([PROCUREMENT_QUERY_KEY, data.project_id], data);
      },
    },
  );
};

export const useCompareProcurement = () => {
  const http = useAgentsHttp();
  return useMutation<
    CompareProcurementResponse,
    Error,
    { project_ids: string[] }
  >((input) =>
    http<CompareProcurementResponse>("/projects/procurement/compare", input),
  );
};
